import { Component, OnInit, Inject, Renderer2 } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Router } from '@angular/router';
import { SmartPaymentLinkService } from '../../shared/services/smart-payment-link.service';
import { UserService } from '../../shared/services/getUser.service';
import { ServiceCenterService } from '../../shared/services/service-center.service';
import { AccountService } from '../../shared/services/account.service';
import { CurrencyService } from '../../shared/services/currency.service';
import { StripeTokenService } from '../../shared/services/stripe-token.service';
import { environment } from '../../../environments/environment';
import { DOCUMENT } from '@angular/common';
import {
  safeParseJson,
  isServiceCenterInactive,
} from '../../shared/utils/restrictions.util';

@Component({
  selector: 'app-short-link-loader',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  template: `
    <div
      style="display:flex;align-items:center;justify-content:center;height:100vh;flex-direction:column"
    >
      <div style="margin-bottom:12px">Cargando link...</div>
      <mat-progress-spinner
        diameter="40"
        mode="indeterminate"
      ></mat-progress-spinner>
    </div>
  `,
})
export class ShortLinkLoaderComponent implements OnInit {
  private currentShortLink: string | null = null;

  constructor(
    private router: Router,
    private smart: SmartPaymentLinkService,
    private userService: UserService,
    private serviceCenterService: ServiceCenterService,
    private accountService: AccountService,
    private currencyService: CurrencyService,
    private stripeTokenService: StripeTokenService,
    private renderer: Renderer2,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  /**
   * Applies the service center's theme/branding/restrictions to the app and
   * returns whether the service center is active. The backend flags an
   * inactive service center via restrictions.inactive === true.
   */
  private async applyServiceCenter(resp: any): Promise<boolean> {
    try {
      const sc = resp?.response?.serviceCenter ?? resp;
      const rawTheme = sc?.theme ?? null;
      const rawRestrictions = sc?.restrictions ?? null;
      const theme: any = safeParseJson(rawTheme);
      const restrictions: any = safeParseJson(rawRestrictions);

      if (isServiceCenterInactive(restrictions)) {
        console.warn(
          '[ShortLinkLoader] Service center is inactive (restrictions.inactive=true), aborting normal flow',
          restrictions,
        );
        return false;
      }

      // Persist restriction flags & bank numbers
      localStorage.setItem('bankNumber', '');
      const bn = restrictions?.bankNumbers;
      if (Array.isArray(bn) || (bn && typeof bn === 'object')) {
        try {
          localStorage.setItem('bankNumber', JSON.stringify(bn));
        } catch {
          localStorage.setItem('bankNumber', '');
        }
      } else if (bn !== null && typeof bn !== 'undefined') {
        localStorage.setItem('bankNumber', String(bn));
      }
      if (typeof restrictions?.useFirstDayRate !== 'undefined')
        localStorage.setItem(
          'useFirstDayRate',
          String(restrictions.useFirstDayRate),
        );
      if (typeof restrictions?.restricRegister !== 'undefined')
        localStorage.setItem(
          'restricRegister',
          String(restrictions.restricRegister),
        );
      if (typeof restrictions?.restricQuote !== 'undefined')
        localStorage.setItem('restricQuote', String(restrictions.restricQuote));
      if (typeof restrictions?.restricMessage !== 'undefined')
        localStorage.setItem(
          'restricMessage',
          String(restrictions.restricMessage),
        );
      if (typeof restrictions?.MercantilButton !== 'undefined')
        localStorage.setItem(
          'MercantilButton',
          String(restrictions.MercantilButton),
        );

      if (sc?.slug) localStorage.setItem('slug', sc.slug);
      if (sc?.id) localStorage.setItem('scid', sc.id);

      // Stripe setting id fetch (publishable key stored by StripeTokenService elsewhere)
      try {
        const settingStripeId =
          sc?.settingStripeId ||
          sc?.setting?.settingStripeId ||
          sc?.setting?.stripeId ||
          null;
        if (settingStripeId) {
          localStorage.setItem('settingStripeId', String(settingStripeId));
          // Fetch the full Stripe settings (publishable key) immediately
          this.stripeTokenService
            .getStripeSetting(String(settingStripeId))
            .subscribe({
              next: (sresp) => {
                console.log(
                  '[ShortLinkLoader] Fetching Stripe settings for settingStripeId success:',
                  sresp,
                );
              },
              error: (serr) => {
                console.warn(
                  '[ShortLinkLoader] Failed to fetch Stripe settings for settingStripeId:',
                  serr,
                );
              },
            });
        }
      } catch (e) {
        console.warn('[ShortLinkLoader] error processing stripe setting id', e);
      }

      if (theme?.primaryColor) {
        document.documentElement.style.setProperty(
          '--theme-color-primary',
          theme.primaryColor,
        );
        // Persist primary color so other screens can restore theme on refresh
        try {
          localStorage.setItem('ColorP', String(theme.primaryColor));
        } catch (e) {
          /* ignore localStorage color write failures */
        }
      }
      if (theme?.primaryTextColor) {
        document.documentElement.style.setProperty(
          '--theme-color-secondary',
          theme.primaryTextColor,
        );
        // Persist secondary color for later reuse
        try {
          localStorage.setItem('ColorS', String(theme.primaryTextColor));
        } catch (e) {
          /* ignore localStorage color write failures */
        }
      }
      if (theme?.logo) localStorage.setItem('logo', theme.logo);

      // Set the document title from the service center name (persist as appName)
      try {
        const scName =
          sc?.name ?? sc?.displayName ?? sc?.title ?? sc?.companyName ?? null;
        if (scName) {
          const nameStr = String(scName);
          try {
            document.title = nameStr;
          } catch (e) {
            /* ignore DOM errors */
          }
          try {
            localStorage.setItem('appName', nameStr);
          } catch (e) {
            /* ignore */
          }
        }
      } catch (e) {
        console.warn(
          '[ShortLinkLoader] failed to set document title from service center',
          e,
        );
      }

      // Set document title from service center name (persist as appName)
      try {
        const scName =
          sc?.name ??
          sc?.title ??
          sc?.displayName ??
          theme?.appName ??
          sc?.serviceCenterName ??
          sc?.companyName ??
          null;
        if (scName) {
          const nameStr = String(scName);
          try {
            document.title = nameStr;
          } catch (e) {
            /* ignore DOM title set errors */
          }
          try {
            localStorage.setItem('appName', nameStr);
          } catch (e) {
            /* ignore localStorage write failures */
          }
        }
      } catch (e) {
        console.warn(
          '[ShortLinkLoader] failed to set document title from service center',
          e,
        );
      }

      // Set favicon: service center favicon (theme.fav) uses API host; fallback uses innerUrl host.
      try {
        const favRaw = theme?.fav || null;
        const scIdCurrent: string =
          sc?.id || sc?.serviceCenterId || sc?.uuid || 'unknown';
        const prevScId = localStorage.getItem('fav_sc_id');
        const prevHref = localStorage.getItem('fav');
        const innerHost = (environment.innerUrl || '').replace(/\/$/, '');
        const apiHost = (environment.api || '').replace(/\/$/, '');
        // Build fallback from innerUrl first (requirement); if absent use api host.
        const fallbackHref = innerHost
          ? innerHost + '/ServlyIcon.png'
          : apiHost
            ? apiHost + '/ServlyIcon.png'
            : this.resolveFavHref('/ServlyIcon.png');

        // When service center supplies a favicon: prefix leading '/' with apiHost (NOT innerUrl).
        let finalHref: string;
        if (favRaw) {
          const favStr = String(favRaw).trim();
          if (/^[a-zA-Z0-9]+:\/\//.test(favStr)) {
            finalHref = favStr; // absolute provided
          } else if (favStr.startsWith('/')) {
            finalHref = apiHost
              ? apiHost + favStr
              : this.resolveFavHref(favStr);
          } else {
            // relative without leading slash; leave as-is (could be CDN or already resolved by index.html script later)
            finalHref = favStr;
          }
        } else {
          finalHref = fallbackHref; // no favicon: use innerUrl fallback
        }
        // Decide if we must force update:
        // 1. Service center changed and new one has no favicon (avoid inheriting previous SC favicon)
        // 2. No previous favicon stored
        // 3. Different href computed
        const scChangedNoFav = !favRaw && prevScId && prevScId !== scIdCurrent;
        const needUpdate =
          scChangedNoFav || !prevHref || prevHref !== finalHref;
        if (needUpdate) {
          this.changeFavicon(finalHref);
          try {
            localStorage.setItem('fav', finalHref);
          } catch {}
          try {
            localStorage.setItem('fav_sc_id', scIdCurrent);
          } catch {}
          if (this.currentShortLink) {
            try {
              localStorage.setItem(
                'fav_for_shortlink_' + this.currentShortLink,
                finalHref,
              );
            } catch {}
          }
        }
      } catch (e) {
        console.warn(
          '[ShortLinkLoader] failed to set title/favicon from service center',
          e,
        );
      }

      // Fetch account details if present
      const accountId = sc?.accountId ?? sc?.account?.id ?? null;
      // Persist the raw account id early so downstream components don't see 'null'
      try {
        localStorage.setItem('owneraccount', String(accountId));
      } catch (e) {
        console.warn(
          '[ShortLinkLoader] failed to persist owneraccount early',
          e,
        );
      }

      // Fetch account-level flags (stripe, transferAndDeposit, settingsMobile)
      try {
        if (accountId) {
          const promises = [];

          // Calling the new AccountService as requested
          promises.push(
            new Promise<void>((resolve) => {
              this.accountService.getAccount(String(accountId)).subscribe({
                next: (accountResp: any) => {
                  console.log(
                    '[ShortLinkLoader] AccountService.getAccount response:',
                    accountResp,
                  );
                  try {
                    if (accountResp) {
                      const multiCurrency = !!accountResp.multiCurrency;
                      const baseCurrencyId = accountResp.baseCurrencyId;
                      localStorage.setItem(
                        'multiCurrency',
                        String(multiCurrency),
                      );
                      if (baseCurrencyId) {
                        localStorage.setItem(
                          'baseCurrencyId',
                          String(baseCurrencyId),
                        );
                      }

                      // Apply conditional currency persistence
                      if (multiCurrency) {
                        const vesId =
                          environment.vesId ||
                          'cfb82af5-8249-4a23-b3fd-b323af04bfe5';
                        localStorage.setItem('accountcurrency', vesId);
                      } else if (baseCurrencyId) {
                        localStorage.setItem(
                          'accountcurrency',
                          String(baseCurrencyId),
                        );
                      }
                    }
                  } catch (e) {
                    console.warn(
                      '[ShortLinkLoader] error persisting multiCurrency/baseCurrencyId',
                      e,
                    );
                  }
                  resolve();
                },
                error: (err) => {
                  console.warn(
                    '[ShortLinkLoader] AccountService.getAccount failed:',
                    err,
                  );
                  resolve();
                },
              });
            }),
          );

          promises.push(
            new Promise<void>((resolve) => {
              this.userService.getAccountById(String(accountId)).subscribe({
                next: (acc: any) => {
                  try {
                    if (typeof acc?.stripe !== 'undefined') {
                      localStorage.setItem('stripe', String(!!acc.stripe));
                    }
                    if (typeof acc?.transferAnddeposit !== 'undefined') {
                      localStorage.setItem(
                        'transferAndDeposit',
                        String(!!acc.transferAnddeposit),
                      );
                    }
                    if (typeof acc?.settingsMobile !== 'undefined') {
                      localStorage.setItem(
                        'settingsMobile',
                        String(!!acc.settingsMobile),
                      );
                    }
                  } catch (e) {
                    console.warn(
                      '[ShortLinkLoader] failed to persist account flags',
                      e,
                    );
                  }
                  resolve();
                },
                error: (err: any) => {
                  console.warn(
                    '[ShortLinkLoader] getAccountById failed (flags not persisted)',
                    err,
                  );
                  resolve();
                },
              });
            }),
          );

          await Promise.all(promises);
        }
      } catch (e) {
        console.warn('[ShortLinkLoader] error calling getAccountById', e);
      }
      return true;
    } catch (e) {
      console.warn('[ShortLinkLoader] applyServiceCenter failed', e);
      return true;
    }
  }

  private changeFavicon(iconPath: string): void {
    try {
      const head =
        this.document.head || this.document.getElementsByTagName('head')[0];
      // Check if the first existing favicon already matches the desired path — if so, avoid touching DOM
      const existingIcons = head.querySelectorAll(
        "link[rel~='icon'], link[rel='shortcut icon']",
      );
      if (existingIcons && existingIcons.length > 0) {
        try {
          const firstHref =
            (existingIcons[0] as HTMLLinkElement).getAttribute('href') || '';
          if (firstHref === iconPath) return; // already set, no-op to avoid reload/flicker
        } catch (e) {
          // continue to replace if any check fails
        }
      }
      // Remove any existing favicon links
      existingIcons.forEach((el) => {
        try {
          this.renderer.removeChild(head, el);
        } catch (e) {
          // ignore
        }
      });

      const link: HTMLLinkElement = this.renderer.createElement('link');
      this.renderer.setAttribute(link, 'type', 'image/x-icon');
      this.renderer.setAttribute(link, 'rel', 'icon');
      this.renderer.setAttribute(link, 'href', iconPath);
      this.renderer.appendChild(head, link);
    } catch (e) {
      console.warn('[ShortLinkLoader] changeFavicon failed', e);
    }
  }

  private getFileUploadHost(): string {
    // Host used for generic resolution (kept for legacy resolveFavHref of relative paths not covered above)
    // We now prefer api for service center favs; innerUrl only for fallback icon path construction earlier.
    try {
      if (environment?.api) return String(environment.api).replace(/\/$/, '');
    } catch (e) {
      /* ignore */
    }
    try {
      if (environment?.innerUrl)
        return String(environment.innerUrl).replace(/\/$/, '');
    } catch (e) {
      /* ignore */
    }
    try {
      return window.location.origin.replace(/\/$/, '');
    } catch (e) {
      return '';
    }
  }

  private resolveFavHref(favStr: string): string {
    try {
      const s = String(favStr || '');
      if (!s) return s;
      if (/^[a-zA-Z0-9]+:\/\//.test(s)) return s; // already absolute
      if (s.startsWith('/')) {
        const host = this.getFileUploadHost();
        if (host) return host + s;
      }
      return s; // leave relative paths as-is
    } catch (e) {
      return String(favStr || '');
    }
  }

  ngOnInit(): void {
    // Clear any previous persisted state before loading a new short link context
    try {
      // Preserve favicon and appName to avoid flicker: read them, clear storage, then restore
      let preservedFav: string | null = null;
      let preservedAppName: string | null = null;
      try {
        preservedFav = localStorage.getItem('fav');
      } catch (e) {
        preservedFav = null;
      }
      try {
        preservedAppName = localStorage.getItem('appName');
      } catch (e) {
        preservedAppName = null;
      }
      localStorage.clear();
      if (preservedFav !== null) {
        try {
          localStorage.setItem('fav', preservedFav);
        } catch (e) {
          /* ignore */
        }
      }
      if (preservedAppName !== null) {
        try {
          localStorage.setItem('appName', preservedAppName);
        } catch (e) {
          /* ignore */
        }
      }
      console.log(
        '[ShortLinkLoader] Cleared previous localStorage state (preserved fav/appName)',
      );
    } catch (e) {
      console.warn('[ShortLinkLoader] Failed to clear localStorage', e);
    }

    const pathname =
      typeof window !== 'undefined' ? window.location.pathname : '';
    const shortLink = pathname.replace(/^\//, '') || '';
    this.currentShortLink = shortLink || null;
    console.log('[ShortLinkLoader] extracted shortLink from URL:', shortLink);
    if (!shortLink) {
      this.router.navigateByUrl('/payment', { replaceUrl: true });
      return;
    }

    this.smart.getByShortLink(shortLink).subscribe({
      next: async (data: any) => {
        console.log('[ShortLinkLoader] getByShortLink response (raw):', data);
        try {
          // If the backend returned an encrypted/base64 string, decrypt it
          if (typeof data === 'string') {
            // AES settings from the environment files
            const KeyBase64 = environment.aesKeyBase64;
            const Iv = environment.aesIv;
            try {
              const decrypted = await this.smart.decryptAesBase64Text(
                data,
                KeyBase64,
                Iv,
              );
              console.log('[ShortLinkLoader] decrypted content:', decrypted);
              // Try to parse JSON
              try {
                // Some responses contain a prefix before the JSON payload, so find
                // the first '{' and parse from there.
                const firstBrace = decrypted.indexOf('{');
                const jsonText =
                  firstBrace >= 0 ? decrypted.slice(firstBrace) : decrypted;
                const parsed = JSON.parse(jsonText);
                data = parsed;
                console.log('[ShortLinkLoader] decrypted JSON parsed:', parsed);
                console.log(
                  '[ShortLinkLoader] PRICE INFO FROM ORDER (decrypted):',
                  {
                    amount: parsed.amount,
                    total: parsed.total,
                    price: parsed.price,
                    unitAmount: parsed.unitAmount,
                    amountInForeignCurrency: parsed.amountInForeignCurrency,
                    foreignAmount: parsed.foreignAmount,
                    subTotal: parsed.subTotal,
                    currency: parsed.currency,
                  },
                );
              } catch (e) {
                console.warn('[ShortLinkLoader] decrypted content not JSON');
              }
            } catch (e) {
              console.error('[ShortLinkLoader] decryption failed', e);
            }
          } else {
            // Log just in case it wasn't encrypted
            console.log(
              '[ShortLinkLoader] PRICE INFO FROM ORDER (unencrypted):',
              {
                amount: data.amount,
                total: data.total,
                price: data.price,
                unitAmount: data.unitAmount,
                amountInForeignCurrency: data.amountInForeignCurrency,
                foreignAmount: data.foreignAmount,
                subTotal: data.subTotal,
                currency: data.currency,
              },
            );
          }

          if (data) {
            // Normalize and persist the requested keys
            const orderCorrelative =
              data.OrderCorrelative ?? data.orderCorrelative ?? null;
            const serviceCenterId =
              data.ServiceCenterId ?? data.serviceCenterId ?? null;
            const customerId = data.CustomerId ?? data.customerId ?? null;
            const emails = data.Emails ?? data.emails ?? null;
            if (orderCorrelative != null)
              localStorage.setItem(
                'orderCorrelative',
                String(orderCorrelative),
              );
            if (serviceCenterId) localStorage.setItem('scid', serviceCenterId);
            if (customerId) localStorage.setItem('cid', customerId);
            if (emails) {
              // Persist the email so the payment flow can read it
              localStorage.setItem('email', emails);
              try {
                // Fetch auth token for the email (email-dos endpoint) and persist it.
                // After token retrieval, fetch service center details, then navigate.
                this.userService.getUserByEmailDos(emails).subscribe(
                  (resp: any) => {
                    try {
                      const token = resp?.token || resp?.accessToken || null;
                      if (token) {
                        localStorage.setItem('auth_token', token);
                        console.log(
                          '[ShortLinkLoader] Stored auth_token from email-dos lookup in localStorage',
                        );
                      } else {
                        console.warn(
                          '[ShortLinkLoader] getUserByEmailDos did not return a token',
                          resp,
                        );
                      }
                    } catch (e) {
                      console.warn(
                        '[ShortLinkLoader] error processing getUserByEmailDos response',
                        e,
                      );
                    }
                    // Now fetch service center info (after token)
                    if (serviceCenterId) {
                      this.serviceCenterService
                        .showServiceCenterId(serviceCenterId)
                        .subscribe(
                          async (scResp: any) => {
                            // First apply service colors etc. (await so we have accountcurrency logic ready)
                            const isActive =
                              await this.applyServiceCenter(scResp);
                            if (!isActive) {
                              this.router.navigateByUrl(
                                '/service-unavailable',
                                { replaceUrl: true },
                              );
                              return;
                            }

                            // Extract the settingStripeId manually to ensure its available
                            const sc =
                              scResp?.response?.serviceCenter ?? scResp;
                            const settingStripeId =
                              sc?.settingStripeId ||
                              sc?.setting?.settingStripeId ||
                              sc?.setting?.stripeId ||
                              null;

                            if (settingStripeId) {
                              console.log(
                                '[ShortLinkLoader] Identified settingStripeId, fetching Stripe configuration BEFORE navigation...',
                                settingStripeId,
                              );
                              this.stripeTokenService
                                .getStripeSetting(String(settingStripeId))
                                .subscribe({
                                  next: (sresp) => {
                                    console.log(
                                      '[ShortLinkLoader] Stripe config pre-fetched successfully. Navigating to /payment.',
                                      sresp,
                                    );
                                    this.router.navigateByUrl('/payment', {
                                      replaceUrl: true,
                                    });
                                  },
                                  error: (serr) => {
                                    console.warn(
                                      '[ShortLinkLoader] Stripe config fetch failed, navigating anyway.',
                                      serr,
                                    );
                                    this.router.navigateByUrl('/payment', {
                                      replaceUrl: true,
                                    });
                                  },
                                });
                            } else {
                              console.log(
                                '[ShortLinkLoader] No settingStripeId found in service center response. Navigating to /payment.',
                              );
                              this.router.navigateByUrl('/payment', {
                                replaceUrl: true,
                              });
                            }
                          },
                          (scErr: any) => {
                            console.warn(
                              '[ShortLinkLoader] showServiceCenterId failed',
                              scErr,
                            );
                            this.router.navigateByUrl('/payment', {
                              replaceUrl: true,
                            });
                          },
                        );
                    } else {
                      // No service center id, just navigate
                      this.router.navigateByUrl('/payment', {
                        replaceUrl: true,
                      });
                    }
                  },
                  (err: any) => {
                    console.warn(
                      '[ShortLinkLoader] getUserByEmailDos request failed',
                      err,
                    );
                    // Navigate even if auth retrieval failed
                    this.router.navigateByUrl('/payment', { replaceUrl: true });
                  },
                );
              } catch (e) {
                console.warn('[ShortLinkLoader] email-dos call failed', e);
                this.router.navigateByUrl('/payment', { replaceUrl: true });
              }
            } else {
              // No email in payload — fetch service center info if available
              if (serviceCenterId) {
                this.serviceCenterService
                  .showServiceCenterId(serviceCenterId)
                  .subscribe(
                    async (scResp: any) => {
                      const isActive = await this.applyServiceCenter(scResp);
                      if (!isActive) {
                        this.router.navigateByUrl('/service-unavailable', {
                          replaceUrl: true,
                        });
                        return;
                      }

                      const sc = scResp?.response?.serviceCenter ?? scResp;
                      const settingStripeId =
                        sc?.settingStripeId ||
                        sc?.setting?.settingStripeId ||
                        sc?.setting?.stripeId ||
                        null;

                      if (settingStripeId) {
                        this.stripeTokenService
                          .getStripeSetting(String(settingStripeId))
                          .subscribe({
                            next: () => {
                              this.router.navigateByUrl('/payment', {
                                replaceUrl: true,
                              });
                            },
                            error: () => {
                              this.router.navigateByUrl('/payment', {
                                replaceUrl: true,
                              });
                            },
                          });
                      } else {
                        this.router.navigateByUrl('/payment', {
                          replaceUrl: true,
                        });
                      }
                    },
                    (scErr: any) => {
                      this.router.navigateByUrl('/payment', {
                        replaceUrl: true,
                      });
                    },
                  );
              } else {
                this.router.navigateByUrl('/payment', { replaceUrl: true });
              }
            }
          }
        } catch (e) {
          console.warn('[ShortLinkLoader] persist failed', e);
        }
        // navigation handled above (either after auth or immediately)
      },
      error: (err) => {
        console.warn('[ShortLinkLoader] getByShortLink failed', err);
        this.router.navigateByUrl('/payment', { replaceUrl: true });
      },
    });
  }
}
